import * as React from "react";
import { useState, useEffect } from "react";
import { useErrorStore } from "../../store/useErrorStore";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Divider from "@mui/joy/Divider";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import FormHelperText from "@mui/joy/FormHelperText";
import Input from "@mui/joy/Input";
import Textarea from "@mui/joy/Textarea";
import Stack from "@mui/joy/Stack";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import states from "../../data/states.json";
import cities from "../../data/state_cities.json";
import Typography from "@mui/joy/Typography";
import Card from "@mui/joy/Card";
import Grid from '@mui/joy/Grid';
import CardActions from "@mui/joy/CardActions";
import CardOverflow from "@mui/joy/CardOverflow";
import Autocomplete from "@mui/joy/Autocomplete";
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import CardContent from '@mui/joy/CardContent';
import Skeleton from '@mui/joy/Skeleton';
import Radio, { radioClasses } from '@mui/joy/Radio';
import RadioGroup from '@mui/joy/RadioGroup';
import Sheet from '@mui/joy/Sheet';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import Avatar from '@mui/joy/Avatar';

dayjs.extend(utc);
dayjs.extend(timezone);

import CircularProgress from '@mui/joy/CircularProgress';
import { CurrencyRupeeRounded } from "@mui/icons-material";

import JoyDatePicker from "../common/JoyDatePicker";

import { fetchCurrentUser, fetchEventsList, createDonation, verifyPayment } from '../../api';
import { useSearchParams } from 'react-router-dom';
import { User as UserInfo, Event as EventItem, Order, PaymentVerifyResponse } from '../../models/all';
import { ref } from "process";

interface DonationFormProps {
   eventId?: number;
   eventData?: EventItem;
}

export default function DonationForm({ eventId: presetEventId, eventData }: DonationFormProps = {}) {
   const [searchParams] = useSearchParams();
   const campaignSlug = searchParams.get('campaign');

   const [user, setUser] = useState<UserInfo>({
      id: 0,
      legalName: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
      pan: '',
      email: '',
      birthdate: '',
      photo_url: '',
      user_type: 'Individual', // Default user type
   });
   const [originalUserData, setOriginalUserData] = useState<UserInfo>({
      id: 0,
      legalName: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
      pan: '',
      email: '',
      birthdate: '',
      photo_url: '',
      user_type: 'Individual', // Default user type
   });
   const [events, setEvents] = useState<EventItem[]>(eventData ? [eventData] : []);
   const [eventId, setEventId] = useState<number | null>(
      presetEventId ?? eventData?.id ?? null
   );
   const [subPurposeName, setSubPurposeName] = useState<string>('');
   const [amount, setAmount] = useState<number | undefined>(undefined);
   const [isAmountEdited, setIsAmountEdited] = useState(false);
   const [showDonateForm, setShowDonateForm] = useState<boolean>(false);
   // Payment status and receipt UI state
   const [paymentStatus, setPaymentStatus] = useState<"success" | "failure" | null>(null);
   const [showReceipt, setShowReceipt] = useState(false);
   const [loading, setLoading] = useState(true);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [isFormEditable, setIsFormEditable] = useState(true);
   const [message, setMessage] = useState<string | null>(null);
   const { error, setError } = useErrorStore();
   // Donor identity state
   const [donorIdentity, setDonorIdentity] = useState<'You' | 'Someone Else'>('You');

   // Safely derive the chosen event and sub-purpose so rendering doesn't blow up
   const selectedEvent = Array.isArray(events) ? events.find((e) => e.id === eventId) : null;
   const selectedSub = selectedEvent?.subPurposes?.find((s) => s.name === subPurposeName) ?? null;

   useEffect(() => {
      if (presetEventId !== undefined) {
         setEventId(presetEventId);
      } else if (eventData) {
         setEventId(eventData.id);
      }
   }, [presetEventId, eventData]);

   useEffect(() => {
      const load = async () => {
         try {
            const u = await fetchCurrentUser();
            setUser(u as UserInfo);
            setOriginalUserData(u as UserInfo);
            if (eventData) {
               setEvents([eventData]);
            } else {
               const ev = await fetchEventsList();
               setEvents(ev as EventItem[]);
            }
         } catch (err) {
            console.error('Failed to load donation form data');
         } finally {
            setLoading(false);
            setShowDonateForm(true);
         }
      };
      load();
   }, [eventData]);

   useEffect(() => {
      if (selectedSub) {
         setAmount(selectedSub.amount || undefined);
      }
   }, [subPurposeName]);

   const loadRazorpayScript = () => {
      return new Promise((resolve) => {
         const script = document.createElement("script");
         script.src = "https://checkout.razorpay.com/v1/checkout.js";
         script.onload = () => resolve(true);
         script.onerror = () => resolve(false);
         document.body.appendChild(script);
      });
   };

   const [errors, setErrors] = useState({
      legalName: "",
      phone: "",
      email: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      pan: "",
      birthdate: "",
      amount: "",
      purpose: "",
      subPurposeName: "",
   });

   useEffect(() => {
      if (message || error) {
         const timer = setTimeout(() => {
            setMessage(null);
            setError(null);
         }, 3000);
         return () => clearTimeout(timer);
      }
   }, [message, error]);

   const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      let errorMsg = "";
      let validatedValue = value;

      switch (name) {
         case "legalName":
            if (!/^[A-Za-z0-9 ]*$/.test(value) || value.length > 24) {
               errorMsg = "Max 24 characters. Only letters, numbers, and spaces allowed.";
            }
            validatedValue = value.slice(0, 24);
            break;

         case "phone":
            if (!/^[1-9][0-9]{9}$/.test(value)) {
               errorMsg = "Must be exactly 10 digits and cannot start with 0.";
            }
            validatedValue = value.slice(0, 10);
            break;

         case "email":
            if (value && !/^[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/.test(value)) {
               errorMsg = "Enter a valid email address.";
            }
            break;

         case "address":
            if (!/^[a-zA-Z0-9 ,\-\/]*$/.test(value) || value.length > 100) {
               errorMsg = "Max 100 characters. Only letters, numbers, ',', '-', and '/' allowed.";
            }
            validatedValue = value.slice(0, 100);
            break;

         case "city":
            validatedValue = value.toUpperCase();
            if (!/^[A-Z ]*$/.test(validatedValue) || validatedValue.length > 20) {
               errorMsg = "Max 20 characters. Only uppercase letters and spaces allowed.";
            }
            validatedValue = validatedValue.slice(0, 20);
            break;

         case "pincode":
            if (!/^[0-9]{0,6}$/.test(value)) {
               errorMsg = "Must be exactly 6 digits.";
            }
            validatedValue = value.slice(0, 6);
            break;

         case "pan":
            validatedValue = value.toUpperCase();
            if (value && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(validatedValue)) {
               errorMsg = "PAN must be exactly 10 characters: 5 letters, 4 digits, 1 letter.";
            }
            validatedValue = validatedValue.slice(0, 10);
            break;

         case "birthdate":
            if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
               errorMsg = "Birthdate must be in YYYY-MM-DD format.";
            }
            break;

         case "amount":
            if (!/^\d*\.?\d*$/.test(value)) {
               return; // Prevent invalid character entry
            }
            if (value && parseFloat(value) < 0) {
               errorMsg = "Enter a valid positive amount.";
            }
            break;

         default:
            break;
      }

      setErrors((prev) => ({ ...prev, [name]: errorMsg }));
      setUser((prev) => ({ ...prev, [name]: validatedValue }));
   };

   const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setIsAmountEdited(true);
      if (!/^\d*\.?\d*$/.test(value)) {
         return; // Prevent invalid character entry
      }
      if (!value) {
         setAmount(undefined);
         setError("Please fill in all required fields.");
         setIsSubmitting(false);
         return;
      }
      if (value && parseFloat(value) < 0) {
         setErrors((prev) => ({ ...prev, amount: "Enter a valid positive amount." }));
      }
      setAmount(value ? parseFloat(value) : undefined);
   }

   const handleSelectChange = (event: any, newValue: string | null, field: string) => {
      if (["paymentMethod", "purpose", "event", "gift", "head"].includes(field)) {
         setUser((prev) => ({ ...prev, [field]: newValue || "" }));
      } else {
         setUser((prev) => ({ ...prev, [field]: newValue || "" }));
      }
   };

   const handleDonate = async () => {
      setIsSubmitting(true);
      // Validate required fields before proceeding
      const requiredFields = [
         { key: 'legalName', label: 'Name' },
         { key: 'phone', label: 'Phone' },
         { key: 'birthdate', label: 'Birthdate' },
         { key: 'address', label: 'Address' },
         { key: 'city', label: 'City' },
         { key: 'state', label: 'State' },
         { key: 'pincode', label: 'Pincode' },
      ];
      const missing = requiredFields.filter(({ key }) => !user[key as keyof typeof user]);
      if (!amount || missing.length > 0 || !eventId || !subPurposeName) {
         setError("All fields are required except email.");
         setIsSubmitting(false);
         return;
      }

      const res = await loadRazorpayScript();
      if (!res) {
         alert("Failed to load Razorpay SDK.");
         return;
      }

      setMessage(null);
      setError(null);

      try {
         const formattedBirthdate = user.birthdate
            ? dayjs(user.birthdate).tz("Asia/Kolkata").format("YYYY-MM-DDTHH:mm:ssZ")
            : null;

         const payload = {
            ...user,
            birthdate: formattedBirthdate,
            amount,
            event: selectedEvent?.title || '',
            purpose: selectedEvent?.purpose || '',
            subPurpose: subPurposeName || '',
            head: selectedEvent?.head || '',
         };
         const finalPayload = campaignSlug ? { ...payload, campaignSlug } : payload;

         const order = await createDonation(finalPayload) as Order;
         const options = {
            key: process.env.REACT_APP_RZPAY_KEY_ID,
            amount: order.amount,
            currency: order.currency,
            name: 'ISKCON Kanpur',
            description: selectedEvent?.title,
            order_id: order.id,
            handler: async (response: any) => {
               // verify signature
               const verifyRes = await verifyPayment(response) as PaymentVerifyResponse;
               if (verifyRes && verifyRes.status === "verified") {
                  setPaymentStatus("success");
               } else {
                  setPaymentStatus("failure");
               }
               setShowDonateForm(false);
               setShowReceipt(true);
            },
            prefill: { name: user.legalName, email: user.email },
         } as any;
         const rzp = new (window as any).Razorpay(options);
         rzp.open();

         setMessage("Donor added successfully!");
      } catch (err) {
         console.error('Donation failed');
      } finally {
         setIsSubmitting(false);
      }
   };

   if (loading) {
      return (
         <Card
            variant="outlined"
            sx={{ width: 'max(600px, 60%)', borderRadius: 10, '--Card-radius': 0 }}
         >
            <CardContent orientation="horizontal">
               <Skeleton variant="rectangular" width={44} height={44} />
               <div>
                  <Skeleton variant="text" width={100} />
                  <Skeleton level="body-sm" variant="text" width={200} />
               </div>
            </CardContent>
            <CardContent sx={{ gap: 0.5, mt: 1 }}>
               <Skeleton level="body-xs" variant="text" width="92%" />
               <Skeleton level="body-xs" variant="text" width="99%" />
               <Skeleton level="body-xs" variant="text" width="96%" />
            </CardContent>
         </Card>
      );
   }
   return (
      <>
         <Card>

            {/* Doner Profile */}
            {!showReceipt ? (
               <>
                  <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                     {/* Donor Identity Radio Group */}
                     <Grid xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
                           <FormLabel sx={{ textAlign: 'center' }}>Who is donating?</FormLabel>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                           {(() => {
                              const donorOptions = [
                                 {
                                    label: `${originalUserData.legalName || 'You'} (You)`,
                                    value: 'You',
                                    avatar: originalUserData.photo_url || undefined,
                                 },
                                 {
                                    label: 'Someone Else',
                                    value: 'Someone Else',
                                    avatar: undefined,
                                 },
                              ];
                              return (
                                 <RadioGroup
                                    aria-label="donor-identity"
                                    value={donorIdentity}
                                    onChange={(e) => {
                                       const selected = e.target.value as 'You' | 'Someone Else';
                                       setDonorIdentity(selected);
                                       if (selected === 'Someone Else') {
                                          setUser({
                                             id: 0,
                                             legalName: '',
                                             phone: '',
                                             address: '',
                                             city: '',
                                             state: '',
                                             pincode: '',
                                             country: 'India',
                                             pan: '',
                                             email: '',
                                             birthdate: '',
                                             photo_url: '',
                                             user_type: 'Individual',
                                          });
                                       } else {
                                          setUser(originalUserData);
                                       }
                                       setErrors({
                                          legalName: "",
                                          phone: "",
                                          email: "",
                                          address: "",
                                          city: "",
                                          state: "",
                                          pincode: "",
                                          pan: "",
                                          birthdate: "",
                                          amount: "",
                                          purpose: "",
                                          subPurposeName: "",
                                       });
                                    }}
                                    overlay
                                    name="donor-identity"
                                    sx={{
                                       flexDirection: 'row',
                                       gap: 2,
                                       [`& .${radioClasses.checked}`]: {
                                          [`& .${radioClasses.action}`]: {
                                             inset: -1,
                                             border: '3px solid',
                                             borderColor: 'primary.500',
                                          },
                                       },
                                       [`& .${radioClasses.radio}`]: {
                                          display: 'contents',
                                          '& > svg': {
                                             zIndex: 2,
                                             position: 'absolute',
                                             top: '-8px',
                                             right: '-8px',
                                             bgcolor: 'background.surface',
                                             borderRadius: '50%',
                                          },
                                       },
                                    }}
                                 >
                                    {donorOptions.map(({ label, value, avatar }) => (
                                       <Sheet
                                          key={value}
                                          variant="outlined"
                                          sx={{
                                             borderRadius: 'md',
                                             boxShadow: 'sm',
                                             display: 'flex',
                                             flexDirection: 'column',
                                             alignItems: 'center',
                                             gap: 1.5,
                                             p: 2,
                                             minWidth: 120,
                                          }}
                                       >
                                          <Radio id={value} value={value} checkedIcon={<CheckCircleRoundedIcon />} />
                                          <Avatar variant="soft" size="sm" src={avatar} />
                                          <FormLabel htmlFor={value}>{label}</FormLabel>
                                       </Sheet>
                                    ))}
                                 </RadioGroup>
                              );
                           })()}
                        </Box>
                     </Grid>
                     <Grid xs={12} sm={4}>
                        <FormControl sx={{ flexGrow: 2 }} error={!!errors.legalName}>
                           <FormLabel>Name</FormLabel>
                           <Input
                              size="sm"
                              placeholder="Full name"
                              name="legalName"
                              value={user.legalName}
                              onChange={handleInputChange}
                              readOnly={!isFormEditable}
                           />
                           {errors.legalName && <FormHelperText>{errors.legalName}</FormHelperText>}
                        </FormControl>
                     </Grid>
                     <Grid xs={12} sm={4}>
                        <FormControl sx={{ flexGrow: 1 }} error={!!errors.phone}>
                           <FormLabel>Phone</FormLabel>
                           <Input
                              size="sm"
                              placeholder="e.g., 1234567890"
                              name="phone"
                              value={user.phone}
                              onChange={handleInputChange}
                              readOnly={!isFormEditable}
                           />
                           {errors.phone && <FormHelperText>{errors.phone}</FormHelperText>}
                        </FormControl>
                     </Grid>
                     <Grid xs={12} sm={4}>
                        <FormControl sx={{ flexGrow: 1 }} error={!!errors.email}>
                           <FormLabel>Email</FormLabel>
                           <Input
                              size="sm"
                              type="email"
                              placeholder="username@example.com"
                              name="email"
                              value={user.email}
                              onChange={handleInputChange}
                              readOnly={!isFormEditable}
                           />
                           {errors.email && <FormHelperText>{errors.email}</FormHelperText>}
                        </FormControl>
                     </Grid>

                     <Grid xs={12} sm={4}>
                        <FormControl error={!!errors.birthdate}>
                           <FormLabel>Birthdate</FormLabel>
                           <JoyDatePicker
                              name="birthdate"
                              value={user.birthdate ? dayjs(user.birthdate) : null}
                              onChange={(newValue) => {
                                 const formattedDate = newValue ? newValue.format("YYYY-MM-DD") : "";
                                 setUser((prev) => ({ ...prev, birthdate: formattedDate }));
                                 setErrors((prev) => ({ ...prev, birthdate: "" }));
                              }}
                              format="YYYY-MM-DD"
                              maxDate={dayjs()}
                              slotProps={{
                                 textField: {
                                    size: "small",
                                    sx: { height: "32px", minHeight: "32px", fontSize: "0.875rem", padding: "5px 10px" },
                                 },
                              }}
                              sx={{ height: "32px", minHeight: "32px" }}
                              disabled={!isFormEditable}
                           />
                           {errors.birthdate && <FormHelperText>{errors.birthdate}</FormHelperText>}
                        </FormControl>
                     </Grid>

                     <Grid xs={12} sm={8}>
                        <FormControl sx={{ flexGrow: 1 }} error={!!errors.address}>
                           <FormLabel>Address</FormLabel>
                           <Input
                              size="sm"
                              placeholder="e.g., 62/72 Street, Area"
                              name="address"
                              value={user.address}
                              onChange={handleInputChange}
                              readOnly={!isFormEditable}
                           />
                           {errors.address && <FormHelperText>{errors.address}</FormHelperText>}
                        </FormControl>
                     </Grid>
                     <Grid xs={12} sm={4}>
                        <FormControl>
                           <FormLabel>State</FormLabel>
                           <Select
                              size="sm"
                              placeholder="Select"
                              value={user.state}
                              onChange={(event, newValue) => handleSelectChange(event, newValue, "state")}
                              disabled={!isFormEditable}
                           >
                              {states.map((state) => (
                                 <Option key={state} value={state}>{state}</Option>
                              ))}
                           </Select>
                        </FormControl>
                     </Grid>
                     <Grid xs={12} sm={4}>
                        <FormControl error={!!errors.city}>
                           <FormLabel>City</FormLabel>
                           <Autocomplete
                              size="sm"
                              freeSolo
                              placeholder="City"
                              value={user.city}
                              options={
                                 Array.from(new Set(
                                    user.state && cities[user.state as keyof typeof cities]
                                       ? cities[user.state as keyof typeof cities]
                                       : Object.values(cities).flat()
                                 ))
                              }
                              onInputChange={(e, newInputValue) => {
                                 setUser((prev) => ({ ...prev, city: newInputValue }));
                                 if (newInputValue.length > 20 || !/^[A-Z ]*$/.test(newInputValue.toUpperCase())) {
                                    setErrors((prev) => ({ ...prev, city: "Max 20 characters. Only uppercase letters and spaces allowed." }));
                                 } else {
                                    setErrors((prev) => ({ ...prev, city: "" }));
                                 }
                              }}
                              getOptionLabel={(option) => typeof option === "string" ? option : ""}
                              disabled={!isFormEditable}
                           />
                           {errors.city && <FormHelperText>{errors.city}</FormHelperText>}
                        </FormControl>
                     </Grid>
                     <Grid xs={12} sm={4}>
                        <FormControl error={!!errors.pincode}>
                           <FormLabel>Pincode</FormLabel>
                           <Input
                              size="sm"
                              placeholder="e.g., 000000"
                              name="pincode"
                              value={user.pincode}
                              onChange={handleInputChange}
                              readOnly={!isFormEditable}
                           />
                           {errors.pincode && <FormHelperText>{errors.pincode}</FormHelperText>}
                        </FormControl>
                     </Grid>
                     <Grid xs={12} sm={4}>
                        <FormControl error={!!errors.pan}>
                           <FormLabel>PAN</FormLabel>
                           <Input
                              size="sm"
                              placeholder="ABCDE1234F"
                              name="pan"
                              value={user.pan}
                              onChange={handleInputChange}
                              readOnly={!isFormEditable}
                           />
                           {errors.pan && <FormHelperText>{errors.pan}</FormHelperText>}
                        </FormControl>
                     </Grid>
                     <Grid xs={12} sm={4}>
                        <FormControl sx={{ flexGrow: 1 }}>
                           <FormLabel>Type</FormLabel>
                           <Select
                              size="sm"
                              placeholder="Select"
                              value={user.user_type}
                              onChange={(event, newValue) => handleSelectChange(event, newValue, "donorType")}
                              disabled={!isFormEditable}
                           >
                              <Option value="Individual">Individual</Option>
                              <Option value="Corporate">Corporate</Option>
                              <Option value="Non Profit">Non Profit</Option>
                              <Option value="Other">Other</Option>
                           </Select>
                        </FormControl>
                     </Grid>
                  </Grid>


                  <Box>
                     <Divider />
                     <Grid container spacing={2} sx={{ flexGrow: 1, my: 1 }}>
                        {!eventData && (
                        <Grid xs={12} sm={4}>
                           <FormControl sx={{ flexGrow: 1 }}>
                              <FormLabel>Purpose</FormLabel>
                              <Select
                                 size="sm"
                                 placeholder="Select"
                                 value={eventId !== null ? String(eventId) : ''}
                                 onChange={(_, v) => { setEventId(Number(v)); setSubPurposeName(''); }}
                                 disabled={presetEventId !== undefined}
                              >
                                 {Array.isArray(events) && events.map((ev) => (
                                    <Option key={ev.id} value={String(ev.id)}>{ev.purpose}</Option>
                                 ))}
                              </Select>
                           </FormControl>
                        </Grid>
                        )}
                        <Grid xs={12} sm={4}>
                           {Array.isArray(selectedEvent?.subPurposes) && (
                              <FormControl sx={{ flexGrow: 1 }}>
                                 <FormLabel>Sub Purpose</FormLabel>
                                 <Select
                                    size="sm"
                                    placeholder="Select"
                                    value={subPurposeName}
                                    onChange={(_, v) => setSubPurposeName(v as string)}>
                                    {selectedEvent?.subPurposes?.map((sp) => (
                                       <Option key={sp.name} value={sp.name}>{sp.name}</Option>
                                    ))}
                                 </Select>
                              </FormControl>
                           )}
                        </Grid>
                        <Grid xs={12} sm={4}>
                           <FormControl sx={{ flexGrow: 1 }}>
                              <FormLabel>Amount</FormLabel>
                              <Input
                                 size="sm"
                                 name="amount"
                                 value={amount ?? ""}
                                 startDecorator={<CurrencyRupeeRounded />}
                                 placeholder="e.g., 10,000"
                                 onChange={handleAmountChange}
                                 readOnly={!!selectedSub?.strict}
                              />
                           </FormControl>
                        </Grid>

                     </Grid>
                     {message && <Typography level="body-sm" sx={{ color: "green", mt: 1 }}>{message}</Typography>}
                     {error && <Typography level="body-sm" sx={{ color: "red", mt: 1 }}>{error}</Typography>}
                     <CardOverflow
                        sx={{ borderTop: "1px solid", borderColor: "divider" }}
                     >
                        <CardActions sx={{ alignSelf: "flex-end", pt: 2 }}>
                           {/* <Button size="sm" variant="outlined" color="neutral">
                                 Cancel
                              </Button> */}
                           <Button
                              size="sm"
                              variant="solid"
                              onClick={handleDonate}
                              disabled={
                                 isSubmitting ||
                                 !amount ||
                                 Object.values(errors).some((error) => error !== "")
                              }
                           >
                              {isSubmitting ? "Processing..." : "Donate"}
                           </Button>
                        </CardActions>
                     </CardOverflow>
                  </Box>
               </>
            )
               : (
                  <Box sx={{ mt: 4, textAlign: 'center' }}>
                     <Typography level="h4" sx={{ color: paymentStatus === "success" ? "green" : "red" }}>
                        {paymentStatus === "success" ? "Payment Successful 🎉" : "Payment Failed ❌"}
                     </Typography>
                     <Typography level="body-sm" sx={{ mt: 1 }}>
                        {paymentStatus === "success"
                           ? "Thank you for your donation!"
                           : "Something went wrong. Please try again."}
                     </Typography>
                     <Button
                        variant="outlined"
                        sx={{ mt: 2 }}
                        onClick={() => {
                           setShowDonateForm(true);
                           setShowReceipt(false);
                           setPaymentStatus(null);
                           setMessage(null);
                           setError(null);
                           setShowDonateForm(true);
                           setUser(originalUserData || { id: 0, legalName: '', phone: '', address: '', city: '', state: '', pincode: '', country: 'India', pan: '', email: '', birthdate: '', photo_url: '', user_type: 'Individual' });
                        }}
                     >
                        {paymentStatus === "success" ? "Make Another Donation" : "Try Again"}
                     </Button>
                  </Box>
               )}
         </Card>

      </>
   );
}

